(function () {
  'use strict';

  var STORAGE_KEY = 'four-nordic-rfq-v1';
  var MAX_ITEMS = 50;
  var memoryItems = [];
  var storageAvailable = true;

  function trackEvent(eventName, parameters) {
    var payload = Object.assign({ event: eventName }, parameters || {});
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('four-nordic:analytics', { detail: payload }));
  }

  function cleanText(value, maxLength) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, maxLength || 300);
  }

  function cleanQuantity(value) {
    var quantity = parseInt(value, 10);
    return Math.min(Math.max(isNaN(quantity) ? 1 : quantity, 1), 9999);
  }

  function cleanCondition(value) {
    return ['any', 'new', 'spare', 'refurbished'].indexOf(value) !== -1 ? value : 'any';
  }

  function cleanUrl(value) {
    if (!value) return '';

    try {
      var url = new URL(value, window.location.origin);
      Array.from(url.searchParams.keys()).forEach(function (key) {
        if (key.charAt(0) === '_') url.searchParams.delete(key);
      });
      return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  function normalizeItem(item) {
    if (!item || typeof item !== 'object') return null;

    var type = item.type === 'manual' ? 'manual' : 'product';
    var reference = cleanText(item.reference || item.sku || item.title, 200);
    var productId = cleanText(item.productId, 50);
    var variantId = cleanText(item.variantId, 50);

    if (!reference && type === 'manual') return null;
    if (!productId && !variantId && type === 'product') return null;

    return {
      id: cleanText(item.id, 260) || (type === 'manual' ? 'manual:' + reference.toLowerCase() : 'product:' + productId + ':' + variantId),
      type: type,
      productId: productId,
      variantId: variantId,
      title: cleanText(item.title, 250),
      variantTitle: cleanText(item.variantTitle, 200),
      sku: cleanText(item.sku, 200),
      reference: reference,
      url: cleanUrl(item.url),
      quantity: cleanQuantity(item.quantity),
      condition: cleanCondition(item.condition)
    };
  }

  function readItems() {
    var storedItems;

    if (!storageAvailable) return memoryItems.slice();

    try {
      storedItems = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (error) {
      storageAvailable = false;
      storedItems = memoryItems;
    }

    if (!Array.isArray(storedItems)) storedItems = [];

    return storedItems.map(normalizeItem).filter(Boolean).slice(0, MAX_ITEMS);
  }

  function saveItems(items) {
    memoryItems = items.map(normalizeItem).filter(Boolean).slice(0, MAX_ITEMS);

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryItems));
    } catch (error) {
      storageAvailable = false;
      // The in-memory fallback still keeps the RFQ functional on the current page.
    }

    renderInterface(memoryItems);
    document.dispatchEvent(new CustomEvent('four-nordic:quote-updated', {
      detail: { items: memoryItems.slice() }
    }));
  }

  function conditionLabels(builder) {
    return {
      any: builder.dataset.anyConditionLabel,
      new: builder.dataset.newConditionLabel,
      spare: builder.dataset.spareConditionLabel,
      refurbished: builder.dataset.refurbishedConditionLabel
    };
  }

  function conditionValueForEmail(value) {
    return {
      any: 'Any acceptable',
      new: 'NEW',
      spare: 'SPARE',
      refurbished: 'Refurbished'
    }[cleanCondition(value)];
  }

  function makeElement(tagName, className, textValue) {
    var element = document.createElement(tagName);
    if (className) element.className = className;
    if (typeof textValue === 'string') element.textContent = textValue;
    return element;
  }

  function appendField(itemElement, item, builder, fieldName, index) {
    var wrapper = makeElement('div', 'rfq-item__field rfq-item__field--' + fieldName);
    var fieldId = 'rfq-item-' + index + '-' + fieldName;
    var label = makeElement('label', '', fieldName === 'quantity' ? builder.dataset.quantityLabel : builder.dataset.conditionLabel);
    label.setAttribute('for', fieldId);
    wrapper.appendChild(label);

    if (fieldName === 'quantity') {
      var input = makeElement('input', 'form__field form__field--text');
      input.id = fieldId;
      input.type = 'number';
      input.min = '1';
      input.max = '9999';
      input.inputMode = 'numeric';
      input.value = item.quantity;
      input.setAttribute('data-rfq-item-quantity', '');
      wrapper.appendChild(input);
    } else {
      var selectWrapper = makeElement('div', 'select-wrapper select-wrapper--primary');
      var select = makeElement('select');
      var labels = conditionLabels(builder);
      select.id = fieldId;
      select.setAttribute('data-rfq-item-condition', '');

      ['any', 'new', 'spare', 'refurbished'].forEach(function (condition) {
        var option = makeElement('option', '', labels[condition]);
        option.value = condition;
        option.selected = item.condition === condition;
        select.appendChild(option);
      });

      var arrow = makeElement('span', 'rfq-item__select-arrow', '⌄');
      arrow.setAttribute('aria-hidden', 'true');
      selectWrapper.appendChild(arrow);
      selectWrapper.appendChild(select);
      wrapper.appendChild(selectWrapper);
    }

    itemElement.appendChild(wrapper);
  }

  function renderItem(item, builder, index) {
    var itemElement = makeElement('article', 'rfq-item');
    itemElement.dataset.rfqItemId = item.id;

    var details = makeElement('div', 'rfq-item__details');
    var title = makeElement('strong', 'rfq-item__title', item.type === 'manual' ? item.reference : item.title || item.reference);
    details.appendChild(title);

    if (item.type === 'product' && item.sku) {
      details.appendChild(makeElement('span', 'rfq-item__meta', builder.dataset.partLabel + ': ' + item.sku));
    }

    if (item.type === 'product' && item.variantTitle && item.variantTitle.toLowerCase() !== 'default title') {
      details.appendChild(makeElement('span', 'rfq-item__meta', item.variantTitle));
    }

    if (item.url) {
      var productLink = makeElement('a', 'rfq-item__link link link--accented', builder.dataset.viewProductLabel);
      productLink.href = item.url;
      details.appendChild(productLink);
    }

    itemElement.appendChild(details);
    appendField(itemElement, item, builder, 'quantity', index);
    appendField(itemElement, item, builder, 'condition', index);

    var removeButton = makeElement('button', 'rfq-item__remove link link--accented', builder.dataset.removeLabel);
    removeButton.type = 'button';
    removeButton.setAttribute('data-rfq-remove-item', '');
    removeButton.setAttribute('aria-label', builder.dataset.removeLabel + ': ' + (item.title || item.reference));
    itemElement.appendChild(removeButton);

    return itemElement;
  }

  function serializeItems(items) {
    if (!items.length) return '';

    var lines = ['FOUR NORDIC QUOTE REQUEST', 'Line items: ' + items.length, ''];

    items.forEach(function (item, index) {
      lines.push((index + 1) + '. ' + (item.type === 'manual' ? 'Manual part request' : 'Website product'));
      if (item.sku || item.reference) lines.push('Part number: ' + (item.sku || item.reference));
      if (item.type === 'product' && item.title) lines.push('Product: ' + item.title);
      if (item.variantTitle && item.variantTitle.toLowerCase() !== 'default title') lines.push('Variant: ' + item.variantTitle);
      lines.push('Quantity: ' + item.quantity);
      lines.push('Preferred condition: ' + conditionValueForEmail(item.condition));
      if (item.url) lines.push('Product URL: ' + item.url);
      lines.push('');
    });

    return lines.join('\n').trim();
  }

  function renderBuilder(builder, items) {
    var container = builder.querySelector('[data-rfq-items]');
    var emptyState = builder.querySelector('[data-rfq-empty]');
    var payload = builder.querySelector('[data-rfq-payload]');
    var itemCount = builder.querySelector('[data-rfq-item-count]');
    if (!container || !emptyState || !payload || !itemCount) return;

    while (container.firstChild) container.removeChild(container.firstChild);
    items.forEach(function (item, index) {
      container.appendChild(renderItem(item, builder, index));
    });

    emptyState.hidden = items.length > 0;
    payload.value = serializeItems(items);
    itemCount.value = String(items.length);
  }

  function renderInterface(items) {
    document.querySelectorAll('[data-rfq-count]').forEach(function (count) {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    });

    document.querySelectorAll('[data-rfq-builder]').forEach(function (builder) {
      renderBuilder(builder, items);
    });
  }

  function showToast(message) {
    var toast = document.querySelector('[data-rfq-toast]');
    if (!toast || !message) return;

    window.clearTimeout(toast._rfqTimer);
    toast.textContent = message;
    toast.classList.add('is-visible');
    toast._rfqTimer = window.setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2600);
  }

  function addProduct(button) {
    var productId = cleanText(button.dataset.rfqProductId, 50);
    var variantId = cleanText(button.dataset.rfqVariantId, 50);
    if (!productId && !variantId) return;

    var quantityInput = button.closest('form') ? button.closest('form').querySelector('[name="quantity"]') : null;
    var quantity = cleanQuantity(quantityInput ? quantityInput.value : 1);
    var itemId = 'product:' + productId + ':' + variantId;
    var items = readItems();
    var existingItem = items.find(function (item) { return item.id === itemId; });
    var itemAdded = false;

    if (existingItem) {
      existingItem.quantity = cleanQuantity(existingItem.quantity + quantity);
      itemAdded = true;
    } else if (items.length < MAX_ITEMS) {
      items.push(normalizeItem({
        id: itemId,
        type: 'product',
        productId: productId,
        variantId: variantId,
        title: button.dataset.rfqProductTitle,
        variantTitle: button.dataset.rfqVariantTitle,
        sku: button.dataset.rfqSku,
        reference: button.dataset.rfqSku || button.dataset.rfqProductTitle,
        url: button.dataset.rfqProductUrl,
        quantity: quantity,
        condition: 'any'
      }));
      itemAdded = true;
    }

    if (!itemAdded) {
      showToast(button.dataset.rfqLimitLabel);
      return;
    }

    saveItems(items);
    trackEvent('rfq_add', {
      item_type: 'product',
      product_id: productId,
      variant_id: variantId,
      quantity: quantity,
      item_count: items.length
    });
    showToast(button.dataset.rfqAddedLabel);

    var label = button.querySelector('[data-rfq-button-label]');
    if (label) {
      window.clearTimeout(button._rfqLabelTimer);
      label.textContent = button.dataset.rfqAddedLabel;
      button._rfqLabelTimer = window.setTimeout(function () {
        label.textContent = button.dataset.rfqAddLabel;
      }, 1800);
    }
  }

  function addManualPart(builder, showValidation) {
    var partInput = builder.querySelector('[data-rfq-manual-part]');
    var quantityInput = builder.querySelector('[data-rfq-manual-quantity]');
    var conditionInput = builder.querySelector('[data-rfq-manual-condition]');
    var errorElement = builder.querySelector('[data-rfq-manual-error]');
    var reference = cleanText(partInput ? partInput.value : '', 200);

    if (!reference) {
      if (showValidation && errorElement) {
        errorElement.textContent = builder.dataset.partRequiredMessage;
        errorElement.hidden = false;
      }
      if (showValidation && partInput) partInput.focus();
      return false;
    }

    if (errorElement) errorElement.hidden = true;

    var quantity = cleanQuantity(quantityInput ? quantityInput.value : 1);
    var condition = cleanCondition(conditionInput ? conditionInput.value : 'any');
    var itemId = 'manual:' + reference.toLowerCase();
    var items = readItems();
    var existingItem = items.find(function (item) { return item.id === itemId; });
    var itemAdded = false;

    if (existingItem) {
      existingItem.quantity = cleanQuantity(existingItem.quantity + quantity);
      existingItem.condition = condition;
      itemAdded = true;
    } else if (items.length < MAX_ITEMS) {
      items.push(normalizeItem({
        id: itemId,
        type: 'manual',
        reference: reference,
        title: reference,
        quantity: quantity,
        condition: condition
      }));
      itemAdded = true;
    }

    if (!itemAdded) {
      if (errorElement) {
        errorElement.textContent = builder.dataset.limitMessage;
        errorElement.hidden = false;
      }
      return false;
    }

    saveItems(items);
    trackEvent('rfq_add', {
      item_type: 'manual',
      quantity: quantity,
      item_count: items.length
    });
    if (partInput) partInput.value = '';
    if (quantityInput) quantityInput.value = '1';
    if (conditionInput) conditionInput.value = 'any';
    return true;
  }


  // Part-number autocomplete via Shopify predictive search (Search & Discovery)
  var suggestState = { timer: null, controller: null, results: [], active: -1 };

  function suggestBox(input) {
    var field = input.closest('.fn-field') || input.parentNode;
    var box = field.querySelector('[data-rfq-suggest]');
    if (!box) {
      field.classList.add('rfq-suggest-host');
      box = makeElement('ul', 'rfq-suggest');
      box.id = 'rfq-suggest-list';
      box.setAttribute('data-rfq-suggest', '');
      box.setAttribute('role', 'listbox');
      box.hidden = true;
      field.appendChild(box);
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-controls', box.id);
      input.setAttribute('aria-expanded', 'false');
    }
    return box;
  }

  function closeSuggest(input) {
    var box = input && suggestBox(input);
    if (box) box.hidden = true;
    if (input) input.setAttribute('aria-expanded', 'false');
    suggestState.active = -1;
  }

  function productSku(product) {
    var v = product.variants && product.variants[0];
    return cleanText((v && v.sku) || product.sku || '', 200);
  }

  function renderSuggest(input, products) {
    var box = suggestBox(input);
    suggestState.results = products;
    suggestState.active = -1;
    while (box.firstChild) box.removeChild(box.firstChild);
    if (!products.length) { closeSuggest(input); return; }
    products.forEach(function (product, index) {
      var li = makeElement('li', 'rfq-suggest__item');
      li.id = 'rfq-suggest-' + index;
      li.setAttribute('role', 'option');
      li.dataset.index = String(index);
      if (product.image || product.featured_image) {
        var img = makeElement('img', 'rfq-suggest__img');
        var src = product.image || (product.featured_image && product.featured_image.url) || '';
        img.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=96';
        img.alt = '';
        img.width = 40; img.height = 40; img.loading = 'lazy';
        li.appendChild(img);
      } else {
        li.appendChild(makeElement('span', 'rfq-suggest__img'));
      }
      var text = makeElement('span', 'rfq-suggest__text');
      var sku = productSku(product);
      if (sku) text.appendChild(makeElement('span', 'rfq-suggest__sku', sku));
      text.appendChild(makeElement('span', 'rfq-suggest__title', cleanText(product.title, 200)));
      li.appendChild(text);
      box.appendChild(li);
    });
    box.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function setActive(input, index) {
    var box = suggestBox(input);
    var items = box.querySelectorAll('.rfq-suggest__item');
    if (!items.length) return;
    suggestState.active = (index + items.length) % items.length;
    items.forEach(function (el, i) { el.classList.toggle('is-active', i === suggestState.active); });
    input.setAttribute('aria-activedescendant', items[suggestState.active].id);
  }

  function fetchSuggest(input) {
    var query = cleanText(input.value, 100);
    window.clearTimeout(suggestState.timer);
    if (query.length < 2) { closeSuggest(input); return; }
    suggestState.timer = window.setTimeout(function () {
      if (suggestState.controller) suggestState.controller.abort();
      suggestState.controller = window.AbortController ? new AbortController() : null;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url = root + 'search/suggest.json?q=' + encodeURIComponent(query) +
        '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,variants.sku,vendor,product_type,tag';
      fetch(url, suggestState.controller ? { signal: suggestState.controller.signal } : {})
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          if (cleanText(input.value, 100) === query) renderSuggest(input, products);
        })
        .catch(function () {});
    }, 160);
  }

  function addSuggestedProduct(input, product) {
    var builder = input.closest('[data-rfq-builder]');
    var quantityInput = builder.querySelector('[data-rfq-manual-quantity]');
    var conditionInput = builder.querySelector('[data-rfq-manual-condition]');
    var errorElement = builder.querySelector('[data-rfq-manual-error]');
    var quantity = cleanQuantity(quantityInput ? quantityInput.value : 1);
    var condition = cleanCondition(conditionInput ? conditionInput.value : 'any');
    closeSuggest(input);

    function commit(full) {
      var variant = (full && full.variants && full.variants[0]) || (product.variants && product.variants[0]) || {};
      var productId = cleanText((full && full.id) || product.id, 50);
      var variantId = cleanText(variant.id, 50);
      var sku = cleanText(variant.sku || productSku(product), 200);
      var itemId = 'product:' + productId + ':' + variantId;
      var items = readItems();
      var existing = items.find(function (item) { return item.id === itemId; });
      if (existing) {
        existing.quantity = cleanQuantity(existing.quantity + quantity);
        existing.condition = condition;
      } else if (items.length < MAX_ITEMS) {
        items.push(normalizeItem({
          id: itemId, type: 'product', productId: productId, variantId: variantId,
          title: (full && full.title) || product.title,
          variantTitle: variant.title, sku: sku, reference: sku || product.title,
          url: product.url, quantity: quantity, condition: condition
        }));
      } else {
        if (errorElement) { errorElement.textContent = builder.dataset.limitMessage; errorElement.hidden = false; }
        return;
      }
      if (errorElement) errorElement.hidden = true;
      saveItems(items);
      trackEvent('rfq_add', { item_type: 'product_lookup', product_id: productId, quantity: quantity, item_count: items.length });
      input.value = '';
      if (quantityInput) quantityInput.value = '1';
      if (conditionInput) conditionInput.value = 'any';
      input.focus();
    }

    var handle = product.handle;
    if (!handle) { commit(null); return; }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(commit)
      .catch(function () { commit(null); });
  }

  function exactMatch(value) {
    var needle = cleanText(value, 200).toLowerCase();
    return suggestState.results.find(function (p) { return productSku(p).toLowerCase() === needle; });
  }

  function updateItemField(target) {
    var itemElement = target.closest('[data-rfq-item-id]');
    if (!itemElement) return;

    var items = readItems();
    var item = items.find(function (candidate) { return candidate.id === itemElement.dataset.rfqItemId; });
    if (!item) return;

    if (target.hasAttribute('data-rfq-item-quantity')) item.quantity = cleanQuantity(target.value);
    if (target.hasAttribute('data-rfq-item-condition')) item.condition = cleanCondition(target.value);
    saveItems(items);
  }

  function removeItem(button) {
    var itemElement = button.closest('[data-rfq-item-id]');
    if (!itemElement) return;
    var items = readItems();
    var removedItem = items.find(function (item) { return item.id === itemElement.dataset.rfqItemId; });
    var remainingItems = items.filter(function (item) { return item.id !== itemElement.dataset.rfqItemId; });
    saveItems(remainingItems);
    trackEvent('rfq_remove', {
      item_type: removedItem ? removedItem.type : 'unknown',
      item_count: remainingItems.length
    });
  }

  function importPartFromUrl() {
    var builder = document.querySelector('[data-rfq-builder]');
    if (!builder) return;

    var params = new URLSearchParams(window.location.search);
    var reference = cleanText(params.get('part'), 200);
    if (!reference) return;

    var items = readItems();
    var referenceLower = reference.toLowerCase();
    var alreadyPresent = items.some(function (item) {
      return cleanText(item.sku || item.reference, 200).toLowerCase() === referenceLower;
    });

    if (!alreadyPresent && items.length < MAX_ITEMS) {
      items.push(normalizeItem({
        id: 'manual:' + referenceLower,
        type: 'manual',
        reference: reference,
        title: reference,
        quantity: cleanQuantity(params.get('quantity')),
        condition: cleanCondition(params.get('condition'))
      }));
      saveItems(items);
    }
  }

  function updateVariantButton(event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    var productForm = event.target && event.target.closest ? event.target.closest('form[action*="/cart/add"]') : null;
    var scope = productForm || (event.target && event.target.closest ? event.target.closest('.product-form') : null) || document;

    scope.querySelectorAll('[data-rfq-add-product]').forEach(function (button) {
      button.dataset.rfqVariantId = String(variant.id);
      button.dataset.rfqVariantTitle = cleanText(variant.title, 200);
      button.dataset.rfqSku = cleanText(variant.sku, 200);

      try {
        var productUrl = new URL(button.dataset.rfqProductUrl || window.location.href, window.location.origin);
        productUrl.searchParams.set('variant', variant.id);
        button.dataset.rfqProductUrl = productUrl.href;
      } catch (error) {
        // Keep the initial product URL if the browser cannot parse it.
      }
    });
  }

  function initialize() {
    if (document.querySelector('[data-rfq-submit-success]')) {
      saveItems([]);
    } else {
      memoryItems = readItems();
      renderInterface(memoryItems);
      importPartFromUrl();
    }
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var productButton = event.target.closest('[data-rfq-add-product]');
    if (productButton) {
      event.preventDefault();
      addProduct(productButton);
      return;
    }

    var manualButton = event.target.closest('[data-rfq-add-manual]');
    if (manualButton) {
      var mb = manualButton.closest('[data-rfq-builder]');
      var partField = mb.querySelector('[data-rfq-manual-part]');
      var match = partField && exactMatch(partField.value);
      if (match) addSuggestedProduct(partField, match);
      else addManualPart(mb, true);
      return;
    }

    var removeButton = event.target.closest('[data-rfq-remove-item]');
    if (removeButton) removeItem(removeButton);
  });

  document.addEventListener('change', function (event) {
    if (event.target.matches('[data-rfq-item-quantity], [data-rfq-item-condition]')) updateItemField(event.target);
  });

  document.addEventListener('keydown', function (event) {
    if (!event.target.matches || !event.target.matches('[data-rfq-manual-part]')) return;
    var input = event.target;
    var box = suggestBox(input);
    var open = !box.hidden && suggestState.results.length;
    if (event.key === 'ArrowDown' && open) { event.preventDefault(); setActive(input, suggestState.active + 1); return; }
    if (event.key === 'ArrowUp' && open) { event.preventDefault(); setActive(input, suggestState.active - 1); return; }
    if (event.key === 'Escape') { closeSuggest(input); return; }
    if (event.key === 'Enter') {
      event.preventDefault();
      var pick = (open && suggestState.active > -1) ? suggestState.results[suggestState.active] : exactMatch(input.value);
      if (pick) addSuggestedProduct(input, pick);
      else { closeSuggest(input); addManualPart(input.closest('[data-rfq-builder]'), true); }
    }
  });

  document.addEventListener('input', function (event) {
    if (event.target.matches && event.target.matches('[data-rfq-manual-part]')) fetchSuggest(event.target);
  });

  // Paste a multi-line list (Excel, email, text) into the part field: one part per line, optional quantity column.
  var QTY_RE = /^\d{1,4}$/;
  function parsePastedList(text) {
    var rows = String(text || '').split(/\r?\n/).map(function (line) {
      var cells = line.split(/\t|;|,(?=\s*\d{1,4}\s*$)/).map(function (c) { return cleanText(c, 200); }).filter(Boolean);
      if (!cells.length) return null;
      var part = cells[0], qty = cells[1];
      if (QTY_RE.test(cells[0]) && cells[1] && !QTY_RE.test(cells[1])) { part = cells[1]; qty = cells[0]; }
      if (cells.length === 1) {
        var m = cells[0].match(/^(\S+)\s+(?:x\s*)?(\d{1,4})$/i);
        if (m) { part = m[1]; qty = m[2]; }
      }
      return { part: part, quantity: QTY_RE.test(qty || '') ? qty : 1 };
    }).filter(Boolean);
    if (rows.length && /^(part|sku|p\/n|pn|item)/i.test(rows[0].part)) rows.shift();
    return rows;
  }

  function resolvePastedParts(references) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    references.reduce(function (chain, reference) {
      return chain.then(function () {
        var url = root + 'search/suggest.json?q=' + encodeURIComponent(reference) +
          '&resources[type]=product&resources[limit]=4&resources[options][fields]=title,variants.sku';
        return fetch(url).then(function (res) { return res.ok ? res.json() : null; }).then(function (data) {
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          var hit = products.find(function (p) { return productSku(p).toLowerCase() === reference.toLowerCase(); });
          if (!hit || !hit.handle) return;
          return fetch(root + 'products/' + encodeURIComponent(hit.handle) + '.js').then(function (res) { return res.ok ? res.json() : null; }).then(function (full) {
            if (!full) return;
            var variant = (full.variants || []).find(function (v) { return cleanText(v.sku, 200).toLowerCase() === reference.toLowerCase(); }) || full.variants[0] || {};
            var items = readItems();
            var index = items.findIndex(function (item) { return item.id === 'manual:' + reference.toLowerCase(); });
            if (index === -1) return;
            var old = items[index];
            var productId = cleanText(full.id, 50), variantId = cleanText(variant.id, 50);
            var itemId = 'product:' + productId + ':' + variantId;
            var existing = items.find(function (item) { return item.id === itemId; });
            if (existing) { existing.quantity = cleanQuantity(existing.quantity + old.quantity); items.splice(index, 1); }
            else items[index] = normalizeItem({ id: itemId, type: 'product', productId: productId, variantId: variantId, title: full.title, variantTitle: variant.title, sku: variant.sku || reference, reference: variant.sku || reference, url: hit.url, quantity: old.quantity, condition: old.condition });
            saveItems(items);
          });
        }).catch(function () {});
      });
    }, Promise.resolve());
  }

  document.addEventListener('paste', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('[data-rfq-manual-part]')) return;
    var text = (event.clipboardData || window.clipboardData).getData('text');
    if (!/[\n\t]/.test(String(text).trim())) return;
    var rows = parsePastedList(text);
    if (rows.length < 1) return;
    event.preventDefault();
    closeSuggest(input);
    var builder = input.closest('[data-rfq-builder]');
    var conditionInput = builder.querySelector('[data-rfq-manual-condition]');
    var errorElement = builder.querySelector('[data-rfq-manual-error]');
    var condition = cleanCondition(conditionInput ? conditionInput.value : 'any');
    var items = readItems(), added = [], full = false;
    rows.forEach(function (row) {
      var id = 'manual:' + row.part.toLowerCase();
      var existing = items.find(function (item) { return item.id === id || cleanText(item.sku, 200).toLowerCase() === row.part.toLowerCase(); });
      if (existing) { existing.quantity = cleanQuantity(existing.quantity + cleanQuantity(row.quantity)); return; }
      if (items.length >= MAX_ITEMS) { full = true; return; }
      items.push(normalizeItem({ id: id, type: 'manual', reference: row.part, title: row.part, quantity: row.quantity, condition: condition }));
      added.push(row.part);
    });
    saveItems(items);
    input.value = '';
    if (errorElement) {
      errorElement.textContent = full ? builder.dataset.limitMessage : '';
      errorElement.hidden = !full;
    }
    showToast((builder.dataset.pastedMessage || '').replace('[count]', rows.length));
    trackEvent('rfq_add', { item_type: 'paste', quantity: rows.length, item_count: items.length });
    resolvePastedParts(added);
  });

  document.addEventListener('mousedown', function (event) {
    var option = event.target.closest && event.target.closest('.rfq-suggest__item');
    if (option) {
      event.preventDefault();
      var input = option.closest('.fn-field').querySelector('[data-rfq-manual-part]');
      addSuggestedProduct(input, suggestState.results[parseInt(option.dataset.index, 10)]);
      return;
    }
    document.querySelectorAll('[data-rfq-manual-part]').forEach(function (input) {
      if (!input.closest('.fn-field').contains(event.target)) closeSuggest(input);
    });
  });

  function fieldValue(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el ? cleanText(el.value, 4000) : '';
  }

  function sendToEndpoint(form, builder, items) {
    var endpoint = cleanUrl(builder.dataset.endpoint);
    var isFormSubmit = /formsubmit\.co/i.test(endpoint);
    if (isFormSubmit && endpoint.indexOf('/ajax/') === -1) endpoint = endpoint.replace(/formsubmit\.co\//i, 'formsubmit.co/ajax/');
    var data = new FormData();
    if (isFormSubmit) {
      data.append('_template', 'table');
      data.append('_captcha', 'false');
      if (builder.dataset.autoresponse) data.append('_autoresponse', builder.dataset.autoresponse);
    }
    var company = fieldValue(form, 'contact[Company]');
    var name = fieldValue(form, 'contact[name]');
    var email = fieldValue(form, 'contact[email]');
    var body = form.querySelector('[name="contact[body]"]');

    data.append('_subject', 'Quote request' + (company ? ' from ' + company : (name ? ' from ' + name : '')) + ' (' + items.length + ' ' + (items.length === 1 ? 'line' : 'lines') + ')');
    data.append('_replyto', email);
    data.append('name', name);
    data.append('email', email);
    data.append('company', company);
    data.append('phone', fieldValue(form, 'contact[Phone]'));
    data.append('country', fieldValue(form, 'contact[Country]'));
    data.append('vat_number', fieldValue(form, 'contact[VAT number]'));
    data.append('needed_by', fieldValue(form, 'contact[Needed by]'));
    data.append('message', body ? String(body.value || '').trim().slice(0, 8000) : '');
    data.append('line_count', String(items.length));
    data.append('part_list', serializeItems(items));
    var esc = function (v) { return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;'); };
    var cell = 'padding:12px;border-bottom:1px solid #DDE3EC;vertical-align:top;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:20px;color:#0E1B33';
    data.append('lines_html', items.map(function (item) {
      var part = esc(item.sku || item.reference);
      if (item.url) part = '<a href="' + esc(item.url) + '" style="color:#3F6AB1;font-weight:bold;text-decoration:none">' + part + '</a>';
      else part = '<strong>' + part + '</strong>';
      return '<tr><td style="' + cell + '">' + part + '</td><td style="' + cell + '">' + esc(item.quantity) +
        '</td><td style="' + cell + '">' + esc(conditionValueForEmail(item.condition)) +
        '</td><td style="' + cell + ';color:#4A5873">' + esc(item.type === 'product' ? item.title : '') + '</td></tr>';
    }).join(''));
    data.append('lines_rows', items.map(function (item) {
      return [item.sku || item.reference, item.quantity, conditionValueForEmail(item.condition), item.type === 'product' ? item.title : '']
        .map(function (cell) { return String(cell || '').replace(/[¦\r\n]+/g, ' ').trim(); }).join('¦');
    }).join('\n'));
    data.append('lines_text', items.map(function (item, i) {
      return (i + 1) + '.  ' + (item.sku || item.reference) + '  ×' + item.quantity + '  ' + conditionValueForEmail(item.condition) +
        (item.type === 'product' && item.title ? '\n    ' + item.title : '') + (item.url ? '\n    ' + item.url : '');
    }).join('\n\n'));
    items.forEach(function (item, i) {
      var n = i + 1;
      data.append('line_' + n + '_part', item.sku || item.reference);
      if (item.type === 'product' && item.title) data.append('line_' + n + '_product', item.title);
      data.append('line_' + n + '_qty', String(item.quantity));
      data.append('line_' + n + '_condition', conditionValueForEmail(item.condition));
      if (item.url) data.append('line_' + n + '_url', item.url);
    });
    data.append('page', window.location.href);
    var gotcha = form.querySelector('[name="_gotcha"]');
    if (gotcha && gotcha.value) { data.append('_gotcha', gotcha.value); data.append('_honey', gotcha.value); }

    // Make webhooks do not send CORS headers: post as a simple request and treat delivery as success.
    if (/(^|\.)(make\.com|integromat\.com)$/i.test(new URL(endpoint).hostname)) {
      return fetch(endpoint, { method: 'POST', body: data, mode: 'no-cors', credentials: 'omit' });
    }
    return fetch(endpoint, { method: 'POST', body: data, headers: { Accept: 'application/json' } })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res; });
  }

  function showSentState(form, builder) {
    var box = makeElement('div', 'fn-quote__success');
    box.setAttribute('role', 'status');
    box.setAttribute('tabindex', '-1');
    box.appendChild(makeElement('div', 'fn-quote__success-title', builder.dataset.successTitle || ''));
    box.appendChild(makeElement('div', 'fn-quote__success-text', builder.dataset.successText || ''));
    var old = form.querySelector('.fn-quote__success, .fn-quote__errors');
    if (old) old.remove();
    form.insertBefore(box, form.firstChild);
    form.querySelectorAll('.fn-quote__fields input, .fn-quote__fields textarea').forEach(function (el) {
      if (el.type !== 'email' && el.name !== 'contact[name]') el.value = '';
    });
    saveItems([]);
    var top = box.getBoundingClientRect().top + window.pageYOffset - 120;
    window.scrollTo({ top: top, behavior: 'smooth' });
    box.focus({ preventScroll: true });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    var builder = form.querySelector && form.querySelector('[data-rfq-builder]');
    if (!builder) return;

    var partInput = builder.querySelector('[data-rfq-manual-part]');
    if (partInput && cleanText(partInput.value, 200)) addManualPart(builder, false);
    var items = readItems();
    renderBuilder(builder, items);
    trackEvent('generate_lead', {
      lead_source: 'rfq',
      item_count: items.length
    });

    if (!cleanUrl(builder.dataset.endpoint) || form._rfqNative) return;
    event.preventDefault();
    // Keep Shopify's contact-form bot protection from submitting the form a second time.
    event.stopImmediatePropagation();
    if (form._rfqSending) return;
    form._rfqSending = true;
    var button = form.querySelector('[type="submit"]');
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }

    sendToEndpoint(form, builder, items)
      .then(function () { showSentState(form, builder); })
      .catch(function () {
        // Fall back to the Shopify contact form so no request is lost.
        form._rfqNative = true;
        HTMLFormElement.prototype.submit.call(form);
      })
      .then(function () {
        form._rfqSending = false;
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
      });
  }, true);

  document.addEventListener('variant:changed', updateVariantButton);
  document.addEventListener('shopify:section:load', function () { renderInterface(readItems()); });
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) renderInterface(readItems());
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();
